/**
 * ClientesModal — base de clientes de Finnegans para el módulo de obras.
 *
 * Se importa desde el export de clientes de Finnegans (.xlsx). Se usa para el
 * selector de cliente al crear una obra. Solo admin (reglas de `clientes`).
 */

import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { importarClientesObras, getClientesResumen } from "../services/obrasFS";

function toDate(ts) {
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return null;
}

// Normaliza encabezados: minúsculas y sin acentos, para tolerar variantes.
function norm(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .trim();
}

function parseExcelClientes(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (rows.length < 2) return [];

  const headers = rows[0].map(norm);
  const iCodigo = headers.findIndex(h => h === "codigo" || h === "id" || h === "cliente codigo");
  const iNombre = headers.findIndex(h => h === "nombre" || h.includes("razon social") || h === "cliente");
  const iRut = headers.findIndex(h => h.includes("identificacion") || h === "rut" || h.includes("documento"));
  if (iCodigo < 0 || iNombre < 0) {
    throw new Error('El archivo no tiene las columnas esperadas ("Codigo" y "Nombre"/"Razon Social") — usá el export de clientes de Finnegans');
  }
  // Candado: no dejar que se importe el archivo de PRODUCTOS por error (tiene
  // columnas como "PrecioBaseVenta"/"Activo" y no "Razon Social"/"Identificacion").
  const esProductos = headers.some(h => h.includes("preciobaseventa") || h.includes("precio base") || h === "activo");
  const esClientes = iRut >= 0 || headers.some(h => h.includes("razon social"));
  if (esProductos && !esClientes) {
    throw new Error('Este parece el archivo de PRODUCTOS (tiene "PrecioBaseVenta"/"Activo"), no el de clientes. Usá el export de CLIENTES de Finnegans (con Razón Social / Identificación).');
  }

  return rows.slice(1)
    .map(r => ({
      codigo: String(r[iCodigo] ?? "").trim(),
      nombre: String(r[iNombre] ?? "").trim(),
      rut: iRut >= 0 ? String(r[iRut] ?? "").trim() : "",
    }))
    .filter(f => f.codigo && f.nombre);
}

export default function ClientesModal({ user, onClose }) {
  const [resumen, setResumen] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    getClientesResumen().then(setResumen).catch(() => setResumen(null));
  }, []);

  async function handleArchivo(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const filas = parseExcelClientes(await file.arrayBuffer());
      if (filas.length === 0) throw new Error("No se encontraron clientes en el archivo");
      const { importados } = await importarClientesObras(filas, user, file.name);
      setMsg({ ok: true, texto: `✅ Se importaron ${importados} clientes` });
      setResumen(await getClientesResumen());
    } catch (err) {
      setMsg({ ok: false, texto: "No se pudo importar: " + (err.message || err) });
    } finally {
      setBusy(false);
    }
  }

  const fecha = toDate(resumen?.actualizadoAt);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 270, padding: 16,
        overflowY: "auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 460, margin: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>👥 Base de clientes</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>

        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>
          Códigos y nombres de los clientes de Finnegans — se usan en el selector
          al crear una obra. Se carga desde el <b>export de clientes de Finnegans</b> (.xlsx).
          Reimportalo cuando se agreguen clientes nuevos.
        </p>

        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 12 }}>
          {resumen === undefined ? "Cargando…"
            : resumen === null ? "Todavía no se importó la base de clientes."
            : <>
                <b>{resumen.cantidad}</b> clientes cargados
                {fecha ? ` · última importación ${fecha.toLocaleDateString("es-UY")} ` : " "}
                {resumen.porNombre ? `por ${resumen.porNombre}` : ""}
                {resumen.archivo ? <div style={{ color: "#94a3b8", fontSize: 12 }}>{resumen.archivo}</div> : null}
              </>}
        </div>

        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleArchivo} />
        <button className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? "Importando…" : "📥 Importar base de clientes"}
        </button>

        {msg && (
          <div style={{
            marginTop: 10, fontSize: 13, padding: "8px 10px", borderRadius: 8,
            background: msg.ok ? "#f0fdf4" : "#fee2e2",
            color: msg.ok ? "#15803d" : "#b91c1c",
          }}>
            {msg.texto}
          </div>
        )}
      </div>
    </div>
  );
}
